// File: src/pages/ForgotPassword.js

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LuArrowLeft } from 'react-icons/lu';
import { FiMail, FiKey, FiLock, FiCheckCircle } from 'react-icons/fi';
import { useToaster } from '../context/ToasterContext';
import { ReactComponent as TribeLogo } from '../assets/TribeLogo.svg';
import { AuthContext } from '../context/AuthContext';

/* ───────────────────────── Visual tokens (UpdateAccount/Register-aligned) */
const COLORS = {
  bg: '#ffffff',
  text: '#0b0b0b',
  subtext: '#374151',
  border: '#e5e7eb',
  brand: '#000000',
  brandDisabled: '#bdbdbd',
  chip: '#f3f4f6',
  error: '#EF4444',
};

const pageStyle = {
  fontFamily:
    "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  backgroundColor: COLORS.bg,
  minHeight: '100vh',
  margin: 0,
  position: 'relative',
  color: COLORS.text,
};

const containerStyle = {
  maxWidth: 460,
  width: '90%',
  margin: '4.5rem auto',
  textAlign: 'center',
  zIndex: 2,
  position: 'relative',
};

const logoSvgStyle = { width: 72, height: 'auto', display: 'block' };

/* ───────────────────────── Header (matches UpdateAccount/Register) */
const headerContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: COLORS.bg,
  position: 'sticky',
  top: 0,
  zIndex: 100,
};

const headerLogoRowStyle = {
  display: 'flex',
  justifyContent: 'center',
  paddingTop: '0.5rem',
};

const headerNavRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  height: 44,
  padding: '0.5rem 1rem',
};

const previousBackButtonStyle = {
  position: 'absolute',
  left: 16,
  background: COLORS.brand,
  color: '#fff',
  borderRadius: 9999,
  width: 40,
  height: 28,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  border: 'none',
};

const pageTitlePlainStyle = {
  fontSize: '0.95rem',
  fontWeight: 900,
  lineHeight: 1,
  textTransform: 'uppercase',
  letterSpacing: 0.2,
  color: COLORS.text,
};

const progressContainerStyle = {
  position: 'absolute',
  right: '1rem',
  top: '50%',
  transform: 'translateY(-50%)',
};

/* ───────────────────────── Progress pill */
function ProgressPill({ step, total }) {
  return (
    <div
      style={{
        background: COLORS.chip,
        border: `1px solid ${COLORS.border}`,
        color: COLORS.text,
        borderRadius: 999,
        padding: '6px 12px',
        fontSize: 12,
        fontWeight: 500,
      }}
    >
      Step <span style={{ fontWeight: 900 }}>{step}</span>/{total}
    </div>
  );
}

/* ───────────────────────── Shared styles (UpdateAccount tone) */
const headingCircleContainer = {
  width: 50,
  height: 50,
  borderRadius: 12,
  background: COLORS.chip,
  display: 'grid',
  placeItems: 'center',
  margin: '0px auto 10px',
};

const headingStyle = {
  fontSize: '1.15rem',
  fontWeight: 900,
  color: COLORS.text,
  margin: '12px 0 8px',
  letterSpacing: 0.1,
};

const subTitleStyle = {
  fontSize: '0.92rem',
  color: COLORS.subtext,
  margin: '0 0 1.25rem 0',
  lineHeight: 1.5,
};

const labelStyle = {
  fontSize: '0.85rem',
  color: '#111827',
  marginBottom: '6px',
  fontWeight: 700,
  display: 'block',
  textAlign: 'left',
  width: '100%',
};

function getInputStyle(isError) {
  return {
    width: '100%',
    padding: '0.65rem 0.7rem',
    fontSize: '0.95rem',
    border: `1px solid ${isError ? COLORS.error : COLORS.border}`,
    borderRadius: 8,
    boxSizing: 'border-box',
    backgroundColor: '#fff',
    color: COLORS.text,
    outline: 'none',
  };
}

function getPrimaryButtonStyle(isActive = true) {
  return {
    width: '100%',
    maxWidth: 300,
    padding: '0.85rem',
    fontSize: 12,
    borderRadius: 10,
    color: '#fff',
    cursor: isActive ? 'pointer' : 'not-allowed',
    fontWeight: 800,
    marginTop: '0rem',
    border: 'none',
    background: isActive ? COLORS.brand : COLORS.brandDisabled,
  };
}

const backBelowButtonStyle = {
  width: '100%',
  maxWidth: 300,
  margin: '0.75rem auto 0',
  fontSize: 12,
  color: COLORS.text,
  backgroundColor: '#fff',
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  cursor: 'pointer',
  fontWeight: 800,
  padding: '0.75rem',
};

const highlightStyle = {
  backgroundColor: COLORS.chip,
  color: COLORS.text,
  fontWeight: 800,
  padding: '2px 8px',
  borderRadius: 6,
  display: 'inline-block',
  marginTop: 10,
};

const alreadyHaveAccountStyle = {
  marginTop: '1rem',
  fontSize: 13,
  color: '#666',
  textAlign: 'center',
};

const loginLinkStyle = {
  marginLeft: 5,
  cursor: 'pointer',
  color: COLORS.text,
  fontWeight: 900,
  fontSize: 13,
};

const shakeKeyframes = `
@keyframes shake {
  0% { transform: translateX(0); }
  20% { transform: translateX(-5px); }
  40% { transform: translateX(5px); }
  60% { transform: translateX(-5px); }
  80% { transform: translateX(5px); }
  100% { transform: translateX(0); }
}
.shake { animation: shake 0.3s ease; }
input:focus { outline: 0px solid #000 !important; }
`;

/* ───────────────────────── Step icons */
function getStepIcon(step) {
  if (step === 1) return <FiMail style={{ fontSize: '1.5rem', color: COLORS.text }} />;
  if (step === 2) return <FiKey style={{ fontSize: '1.5rem', color: COLORS.text }} />;
  if (step === 3) return <FiLock style={{ fontSize: '1.5rem', color: COLORS.text }} />;
  return <FiCheckCircle style={{ fontSize: '1.5rem', color: COLORS.text }} />;
}

/* ───────────────────────── Component */
export default function ForgotPassword() {
  const navigate = useNavigate();
  const { addToast } = useToaster();

  const [step, setStep] = useState(1);
  // No separate success screen; flow is 1) email -> 2) code -> 3) reset -> redirect
  const totalSteps = 3;

  const [email, setEmail] = useState('');
  const [codeDigits, setCodeDigits] = useState(['', '', '']);
  const [shakeInputs, setShakeInputs] = useState(false);
  const [invalidCodeAttempt, setInvalidCodeAttempt] = useState(false);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [shakePasswords, setShakePasswords] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [confirmPasswordError, setConfirmPasswordError] = useState(false);

  function handleNavBack() {
    navigate('/login');
  }
  function handleBack() {
    setStep((prev) => Math.max(1, prev - 1));
  }

  /* ── Step 1: send email */
  const emailFilled = !!email.trim();
  async function handleSendEmail() {
    if (!emailFilled) {
      addToast('Please enter your email.', 'error');
      return;
    }
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/users/forgotPasswordSend`, { email });
      setStep(2);
    } catch (err) {
      addToast(err.response?.data?.message || 'Error sending reset code', 'error');
    }
  }

  /* ── Step 2: verify code */
  const codeFilled = codeDigits.every((d) => d !== '');
  async function verifyCodeOnServer(fullCode) {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/users/forgotPasswordVerify`, {
        email,
        code: fullCode,
      });
      setInvalidCodeAttempt(false);
      setStep(3);
    } catch (err) {
      addToast('Invalid code', 'error');
      setInvalidCodeAttempt(true);
      setShakeInputs(true);
      setTimeout(() => {
        setCodeDigits(['', '', '']);
        setShakeInputs(false);
        setInvalidCodeAttempt(false);
      }, 300);
    }
  }

  async function handleResendCode() {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/users/forgotPasswordSend`, { email });
      addToast('Verification code resent successfully!', 'success');
      setInvalidCodeAttempt(false);
      setCodeDigits(['', '', '']);
    } catch (err) {
      addToast(err.response?.data?.message || 'Error re-sending code', 'error');
    }
  }

  /* ── Step 3: finalize new password */
  const passwordsFilled = !!password.trim() && !!confirmPassword.trim();
  async function handleResetPassword() {
    if (!passwordsFilled) {
      addToast('Please fill out both fields.', 'error');
      return;
    }
    setPasswordError(false);
    setConfirmPasswordError(false);

    if (password !== confirmPassword) {
      setPassword('');
      setConfirmPassword('');
      setPasswordError(true);
      setConfirmPasswordError(true);
      setShakePasswords(true);
      addToast('The two fields do not match.', 'error');
      setTimeout(() => {
        setShakePasswords(false);
        setPasswordError(false);
        setConfirmPasswordError(false);
      }, 300);
      return;
    }

    try {
      const fullCode = codeDigits.join('');
      await axios.post(`${process.env.REACT_APP_API_URL}/users/forgotPasswordSet`, {
        email,
        code: fullCode,
        newPassword: password,
        confirmPassword: confirmPassword,
      });

      addToast('Your password has been updated successfully.', 'success');
      navigate('/login');
    } catch (err) {
      setPassword('');
      setConfirmPassword('');
      setPasswordError(true);
      setConfirmPasswordError(true);
      setShakePasswords(true);
      addToast(err.response?.data?.message || 'Error resetting password', 'error');
      setTimeout(() => {
        setShakePasswords(false);
        setPasswordError(false);
        setConfirmPasswordError(false);
      }, 300);
    }
  }

  function renderStepContent() {
    const icon = getStepIcon(step);

    switch (step) {
      case 1:
        return (
          <div>
            <style>{shakeKeyframes}</style>
            <div style={headingCircleContainer}>{icon}</div>
            <h2 style={headingStyle}>Forgot Password</h2>
            <p style={subTitleStyle}>Enter your email to receive a verification code.</p>

            <div style={{ textAlign: 'left', maxWidth: 300, margin: '0 auto 1.25rem' }}>
              <label style={labelStyle}>Email</label>
              <input
                type="email"
                placeholder="you@example.com"
                style={getInputStyle(false)}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSendEmail()}
              />
            </div>

            <button
              onClick={handleSendEmail}
              disabled={!emailFilled}
              style={getPrimaryButtonStyle(emailFilled)}
            >
              Continue
            </button>

            <div style={alreadyHaveAccountStyle}>
              Remember your password?
              <span onClick={() => navigate('/login')} style={loginLinkStyle}>
                Log in
              </span>
            </div>
          </div>
        );

      case 2:
        return (
          <div style={{ textAlign: 'center', maxWidth: 460, margin: '0 auto' }}>
            <style>{shakeKeyframes}</style>
            <div style={headingCircleContainer}>{icon}</div>
            <h2 style={headingStyle}>Verification Code</h2>
            <p style={subTitleStyle}>
              Enter the 3-digit code we sent to <br />
              <span style={highlightStyle}>{email}</span>
            </p>

            <div
              className={shakeInputs ? 'shake' : ''}
              style={{ display: 'flex', justifyContent: 'center', gap: 10, marginBottom: '1.25rem' }}
            >
              {codeDigits.map((digit, idx) => {
                const borderColor = invalidCodeAttempt ? COLORS.error : COLORS.border;
                return (
                  <input
                    key={idx}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="one-time-code"
                    maxLength={1}
                    value={digit}
                    placeholder="0" // 👈 visually shows “000” across three boxes
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(-1);
                      const next = [...codeDigits];
                      next[idx] = val;
                      setCodeDigits(next);
                      if (val && idx < codeDigits.length - 1) {
                        const el = e.target.nextElementSibling;
                        if (el) el.focus();
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Backspace' && !codeDigits[idx] && idx > 0) {
                        e.preventDefault();
                        const prev = e.currentTarget.previousElementSibling;
                        if (prev) prev.focus();
                      }
                      if (e.key === 'Enter' && codeFilled) {
                        verifyCodeOnServer(codeDigits.join(''));
                      }
                    }}
                    style={{
                      width: 52,
                      height: 52,
                      fontSize: '1.4rem',
                      textAlign: 'center',
                      border: `1.5px solid ${borderColor}`,
                      borderRadius: 10,
                      background: '#fff',
                      color: COLORS.text,
                    }}
                  />
                );
              })}
            </div>

            <p style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: '0.75rem', marginBottom: '1.75rem' }}>
              Didn’t get the code?{' '}
              <span
                onClick={handleResendCode}
                style={{ cursor: 'pointer', color: COLORS.text, fontWeight: 800 }}
              >
                Click to resend
              </span>
            </p>

            <button
              onClick={() => codeFilled && verifyCodeOnServer(codeDigits.join(''))}
              disabled={!codeFilled}
              style={getPrimaryButtonStyle(codeFilled)}
            >
              Continue
            </button>
            <button onClick={handleBack} style={backBelowButtonStyle}>
              Back
            </button>
          </div>
        );

      case 3:
        return (
          <div style={{ textAlign: 'center', maxWidth: 460, margin: '0 auto' }}>
            <style>{shakeKeyframes}</style>
            <div style={headingCircleContainer}>{icon}</div>
            <h2 style={headingStyle}>Set New Password</h2>
            <p style={subTitleStyle}>Enter and confirm your new password.</p>

            <div
              className={shakePasswords ? 'shake' : ''}
              style={{ textAlign: 'left', maxWidth: 300, margin: '0 auto 0.75rem' }}
            >
              <label style={labelStyle}>New Password</label>
              <input
                type="password"
                placeholder="••••••"
                style={getInputStyle(passwordError)}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleResetPassword()}
              />
            </div>

            <div
              className={shakePasswords ? 'shake' : ''}
              style={{ textAlign: 'left', maxWidth: 300, margin: '0 auto 1.25rem' }}
            >
              <label style={labelStyle}>Confirm Password</label>
              <input
                type="password"
                placeholder="••••••"
                style={getInputStyle(confirmPasswordError)}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleResetPassword()}
              />
            </div>

            <button
              onClick={handleResetPassword}
              disabled={!passwordsFilled}
              style={getPrimaryButtonStyle(passwordsFilled)}
            >
              Reset Password
            </button>
            <button onClick={handleBack} style={backBelowButtonStyle}>
              Back
            </button>
          </div>
        );

      default:
        return null;
    }
  }

  return (
    <div style={pageStyle}>
      {/* Header */}
      {step >= 1 && step <= 3 && (
        <header style={headerContainerStyle}>
          <div style={headerLogoRowStyle}>
            <TribeLogo style={logoSvgStyle} />
          </div>

          <div style={headerNavRowStyle}>
            <button onClick={handleNavBack} style={previousBackButtonStyle}>
              <LuArrowLeft size={16} color="#fff" />
            </button>

            <div style={pageTitlePlainStyle}>Forgot Password</div>

            <div style={progressContainerStyle}>
              <ProgressPill step={step} total={totalSteps} />
            </div>
          </div>
        </header>
      )}

      <div style={containerStyle}>{renderStepContent()}</div>
    </div>
  );
}
